/**
 * This is to get current offers that are available in an ordering flow
 */
import { createSelector } from 'reselect';
import * as lodash from 'lodash';
import enhanceOfferTargetPLUs from '../utils/ordering/enhanceOfferTargetPLUs';

import getCurrentOrder from './getCurrentOrder';
import getBuffer from './getBuffer';
import getItems, { $getItems } from './getItems';
import getSaleType, { $getSaleType } from './getSaleType';
import { SALE_TYPE } from '../constants/saleType';
import {
  getShowExpiredCoupons,
  getShowRedeemedCoupons,
  getShowUnstartedCoupons,
} from './config';

const filterOffers = (
  offers: Offer[],
  items: Items,
  saleType: SALE_TYPE,
  showExpiredCoupons: boolean,
  showRedeemedCoupons: boolean,
  showUnstartedCoupons: boolean,
  locationId: string | null,
) => {
  const currentDateTime = new Date().getTime();
  return (
    offers
      .filter(offer => {
        // Filtering based on location
        if (!offer.availableAtLocation && locationId !== null) return false;

        // Filtering based on time
        if (!offer.availableAtTime && locationId !== null) return false;

        // Filtering based on expiry date
        if (
          !showExpiredCoupons &&
          offer.expiryDate &&
          new Date(offer.expiryDate).getTime() < currentDateTime
        )
          return false;

        // Filtering based on redemption
        // offer.redeemed will remain false for multi-use coupons even if it has been redeemed.
        // This is good because we only want to filter out redeemed single-use coupons.
        if (!showRedeemedCoupons && offer.redeemed) return false;

        // Filtering based on unstarted
        if (
          !showUnstartedCoupons &&
          offer.startDate &&
          new Date(offer.startDate).getTime() > currentDateTime
        )
          return false;

        // Filtering based on saleType
        if (offer.saleTypes.length && !offer.saleTypes.includes(saleType))
          return false;

        return true;
      })
      // bruh, precomputing PLUs in this step is not at all clear Max, should be documented at least
      .map(offer => enhanceOfferTargetPLUs(offer, items))
  );
};

// Narrow sub-selectors that only track the specific fields this file needs.
// Using getCurrentOrder directly as a selector input means ANY currentOrder
// mutation (deliveryFee, deliverySurcharges, etc.) invalidates the cache —
// these sub-selectors stay stable when unrelated fields change.
const getOrderOffers = createSelector([getCurrentOrder], order => order.offers);
const getOrderLocationId = createSelector([getCurrentOrder], order => order.locationId);
const getBufferOffers = createSelector([getBuffer], buffer => buffer.offers);
const getBufferLocationId = createSelector([getBuffer], buffer => buffer.locationId);

// Memoized union: a new array is only produced when the actual offer lists change,
// not on every unrelated currentOrder mutation.
const getMergedOffers = createSelector(
  [getOrderOffers, getBufferOffers],
  (orderOffers, bufferOffers) => lodash.unionBy(orderOffers, bufferOffers || [], 'id'),
);

export const $getOffers = createSelector(
  [
    getOrderOffers,
    getBufferOffers,
    getOrderLocationId,
    getBufferLocationId,
    $getItems,
    $getSaleType,
    getShowExpiredCoupons,
    getShowRedeemedCoupons,
    getShowUnstartedCoupons,
  ],
  (
    orderOffers,
    bufferOffers,
    orderLocationId,
    bufferLocationId,
    items,
    saleType,
    showExpiredCoupons,
    showRedeemedCoupons,
    showUnstartedCoupons,
  ): Offer[] => {
    if (!items) {
      return [];
    }

    return filterOffers(
      bufferOffers || orderOffers,
      items,
      saleType,
      showExpiredCoupons,
      showRedeemedCoupons,
      showUnstartedCoupons,
      bufferLocationId || orderLocationId,
    );
  },
);


export default createSelector(
  [
    getMergedOffers,
    getOrderLocationId,
    getItems,
    getSaleType,
    getShowExpiredCoupons,
    getShowRedeemedCoupons,
    getShowUnstartedCoupons,
  ],
  (
    mergedOffers,
    locationId,
    items,
    saleType,
    showExpiredCoupons,
    showRedeemedCoupons,
    showUnstartedCoupons,
  ): Offer[] =>
    filterOffers(
      mergedOffers,
      items ?? {},
      saleType,
      showExpiredCoupons,
      showRedeemedCoupons,
      showUnstartedCoupons,
      locationId,
    ),
);
